import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Alerta, Carregando, SemParametros } from "../componentes/HubDpUi";
import { lerParametrosFolha } from "../lib/hubDp";
import { reais, type ParametrosFolha } from "../lib/folhaDp";
import DpHolerite from "./DpHolerite";
import DpFerias from "./DpFerias";
import DpEncargos from "./DpEncargos";
import DpEsocial from "./DpEsocial";
import DpAdmissao from "./DpAdmissao";

// O HUB DP: as cinco rotinas de Departamento Pessoal, despachadas pelo primeiro
// segmento depois do slug — `holerite`, `ferias-13`, `encargos`, `esocial` e
// `admissao`. O segundo segmento é a ABA do módulo.
//
// ---------------------------------------------------------------------------
// De onde isto veio
// ---------------------------------------------------------------------------
// Portado do app externo `hubdp.contabilidadefacilitada.com` (Lovable, TanStack
// Start), a pedido de Victor em 24/08. O que mudou no caminho, e por quê, está em
// `docs/runbooks/hub-dp.md`. Em resumo: as tabelas de INSS, IRRF, salário mínimo e
// deduções saíram do código para `dp_parametros`/`dp_faixas` (000108), com tela de
// admin — na origem estavam cravadas num `.ts`, e o INSS já era de 2026 enquanto o
// IRRF continuava o de 2025 sem ninguém notar; os cinco geradores, que eram texto
// ASCII copiável, viraram três folhas imprimíveis e dois textos, pelo DESTINO de
// cada documento; as abas viraram sub-rotas; e a versão do leiaute do eSocial
// deixou de ser cravada no componente.
//
// ---------------------------------------------------------------------------
// O redesenho de 25/08
// ---------------------------------------------------------------------------
// A primeira versão desta tela era uma grade de cinco caixas iguais, e o resultado
// de cada módulo era uma pilha de outras cinco. Victor viu e disse que não
// agradou; o diagnóstico está no cabeçalho de `HubDpUi.tsx`. O que mudou: hero
// navy com o número herói vivo, a composição em barra proporcional, a tabela
// progressiva desenhada em escala, e o holerite em duas colunas como o papel de
// verdade.
//
// ---------------------------------------------------------------------------
// Estado EFÊMERO, e sem tabela nenhuma
// ---------------------------------------------------------------------------
// Nada do que o aluno preenche é gravado. É como o app de origem funciona, e o
// corte da 000098 (Simulador MEI) diz que ferramenta efêmera não ganha tabela de
// estado. A única leitura do banco são os parâmetros da folha — carregados UMA vez
// aqui e passados aos módulos, em vez de cinco chamadas. (A exceção é o checklist
// de admissão, que guarda as marcas no `localStorage` do próprio navegador: uma
// admissão leva dias, e perder as marcas a cada visita tornaria a aba inútil.)
//
// ---------------------------------------------------------------------------
// Os cinco segmentos são RESERVADOS
// ---------------------------------------------------------------------------
// `holerite`, `ferias-13`, `encargos`, `esocial` e `admissao` são palavras
// reservadas desta ferramenta. Se um dia o HUB DP ganhar conteúdo pelo motor da
// 000073, uma seção com um desses endereços ficaria inalcançável — é a mesma
// consequência registrada no runbook do MEI PRO para `gestao` e `ocupacoes`.
//
// E NÃO há `<Rodape />` aqui: a `Moldura` de `Ferramenta.tsx` já põe um. Ter os
// dois desenhava o rodapé DUAS VEZES em toda tela da ferramenta — visível nas
// capturas de 25/08, e o mesmo defeito estava nas telas do Simulador MEI e do
// Gerador de Currículos, dos portes do dia anterior.

export default function HubDp() {
  const { "*": resto = "" } = useParams();
  const segmentos = resto.split("/").filter(Boolean);
  const modulo = segmentos[0] ?? null;
  const aba = segmentos[1] ?? "";

  const [p, setP] = useState<ParametrosFolha | null>(null);
  const [fase, setFase] = useState<"carregando" | "pronto" | "erro">("carregando");

  useEffect(() => {
    let vivo = true;
    lerParametrosFolha().then((r) => {
      if (!vivo) return;
      setP(r);
      setFase(r ? "pronto" : "erro");
    });
    return () => {
      vivo = false;
    };
  }, []);

  if (!modulo) return <Entrada p={p} />;

  const Tela = MODULOS[modulo];
  if (!Tela) return <NaoExiste segmento={modulo} />;

  return (
    <main className="mx-auto max-w-6xl px-4 py-7 sm:px-6">
      {fase === "carregando" ? (
        <Carregando />
      ) : fase === "erro" || !p ? (
        <SemParametros />
      ) : (
        <Tela p={p} aba={aba} />
      )}
    </main>
  );
}

/** Slug do módulo → a tela dele. Módulo novo entra aqui e na lista `ROTINAS`. */
const MODULOS: Record<string, (props: { p: ParametrosFolha; aba: string }) => React.ReactElement> = {
  holerite: DpHolerite,
  "ferias-13": DpFerias,
  encargos: DpEncargos,
  esocial: DpEsocial,
  admissao: DpAdmissao,
};

/**
 * As cinco rotinas.
 *
 * `largo` marca as duas que ocupam meia largura na primeira fileira — o Holerite
 * porque é o módulo-raiz (os outros quatro usam o cálculo dele) e Férias porque é
 * o segundo mais procurado. Os três restantes dividem a segunda fileira em
 * terços.
 *
 * A grade é de 6 colunas justamente por isso: 3+3 na primeira e 2+2+2 na segunda
 * fecham exatas. A primeira versão usava `sm:grid-cols-2` com cinco itens, e o
 * quinto ficava sozinho com um buraco do tamanho de um card ao lado.
 */
const ROTINAS = [
  {
    slug: "holerite",
    numero: "01",
    largo: true,
    titulo: "Holerite",
    chamada: "A folha do mês, do salário ao líquido",
    descricao:
      "Horas extras, adicional noturno, periculosidade e insalubridade. INSS progressivo, " +
      "IRRF nos dois modelos — com o mais vantajoso indicado — e o FGTS.",
    marcas: ["INSS progressivo", "IRRF Modelo 1 × 2", "recibo imprimível"],
    cta: "Calcular a folha",
  },
  {
    slug: "ferias-13",
    numero: "02",
    largo: true,
    titulo: "Férias e 13º",
    chamada: "O terço, o abono e as duas parcelas",
    descricao:
      "Férias com o terço constitucional e o abono pecuniário — que não sofre INSS nem IRRF. " +
      "E o 13º em avos, com os prazos de cada parcela.",
    marcas: ["abono fora da base", "13º em avos", "aviso de férias"],
    cta: "Calcular férias ou 13º",
  },
  {
    slug: "encargos",
    numero: "03",
    largo: false,
    titulo: "Encargos e FGTS",
    chamada: "O custo além do salário",
    descricao:
      "INSS patronal de 20% ou CPRB, RAT × FAP e terceiros. E o FGTS na rescisão, por motivo.",
    marcas: ["20% × CPRB", "multa de 40%"],
    cta: "Calcular encargos",
  },
  {
    slug: "esocial",
    numero: "04",
    largo: false,
    titulo: "eSocial",
    chamada: "O que enviar, e até quando",
    descricao:
      "O cronograma dos 16 eventos de uma competência, com prazos. Mais as 10 rejeições mais comuns.",
    marcas: ["16 eventos", "10 rejeições"],
    cta: "Montar a competência",
  },
  {
    slug: "admissao",
    numero: "05",
    largo: false,
    titulo: "Admissão",
    chamada: "Do aceite ao primeiro fechamento",
    descricao:
      "O roteiro em três fases, com os itens críticos destacados. E o contrato nas cinco modalidades.",
    marcas: ["19 itens", "5 modalidades"],
    cta: "Abrir o roteiro",
  },
];

function Entrada({ p }: { p: ParametrosFolha | null }) {
  return (
    <main>
      {/* O hero em painel navy: é a linguagem do hub para território — o hero da
          inicial, o card de Aulas e a Faculdade inteira usam a mesma peça. */}
      <div className="painel-navy px-4 pt-10 pb-20 sm:px-6 sm:pt-12">
        <div className="mx-auto max-w-6xl">
          <p className="rise text-xs font-semibold tracking-[0.2em] text-navy-mute uppercase">
            Departamento Pessoal · Legislação trabalhista
          </p>
          <h1
            className="rise mt-2 font-display text-3xl font-bold text-navy-ink sm:text-5xl"
            style={{ "--i": 1 } as React.CSSProperties}
          >
            HUB DP
          </h1>
          <p
            className="rise mt-3 max-w-2xl text-base leading-relaxed text-navy-mute"
            style={{ "--i": 2 } as React.CSSProperties}
          >
            As rotinas do dia a dia com a conta feita passo a passo, a legislação ao lado e o
            documento no fim. Cinco módulos, e nada do que você digitar é gravado.
          </p>

          {/* Os três fatos, sobre navy. Só entram porque são DADO real: as
              referências vêm do banco, e a contagem sai da lista de rotinas. */}
          <dl
            className="rise mt-7 flex flex-wrap gap-x-8 gap-y-4"
            style={{ "--i": 3 } as React.CSSProperties}
          >
            <Fato rotulo="Módulos" valor={String(ROTINAS.length)} />
            <Fato rotulo="Tabela do INSS" valor={p ? p.referenciaInss : "…"} />
            <Fato
              rotulo="Teto do INSS"
              valor={p ? reais(p.inssTeto) : "…"}
              nota={p ? `desconto máximo de ${reais(p.inssDescontoMaximo)}` : undefined}
            />
          </dl>
        </div>
      </div>

      {/* Os cartões sobem sobre o painel — o mesmo recorte da inicial do hub. */}
      <div className="mx-auto -mt-12 max-w-6xl px-4 pb-4 sm:px-6">
        <ul className="grid gap-4 sm:grid-cols-6">
          {ROTINAS.map((r, i) => (
            <li
              key={r.slug}
              className={r.largo ? "sm:col-span-3" : "sm:col-span-2"}
            >
              {/*
                `surface porta` e não só `porta`: a utility `porta` é COMPORTAMENTO
                (o hover que levanta, a soleira laranja acendendo) e não tem fundo
                próprio — quem dá fundo é a `surface`, e é assim que `Inicio.tsx` a
                usa. Sem ela os cards ficam transparentes, e como eles SOBEM sobre
                o painel navy (`-mt-12`), o topo de cada um exibia `text-titulo`
                escuro sobre azul-escuro: título ilegível, visto na captura.
                É a cicatriz dos "cards em escada do login" pelo caminho inverso —
                lá o texto clareou no escuro, aqui ele escureceu sobre navy.
              */}
              <Link
                to={`/ferramenta/hub-dp/${r.slug}`}
                className="surface porta rise group flex h-full flex-col p-5"
                style={{ "--i": Math.min(i, 5) } as React.CSSProperties}
              >
                <div className="flex items-baseline gap-2.5">
                  <span
                    aria-hidden="true"
                    className="font-display text-2xl font-bold tabular-nums text-laranja"
                  >
                    {r.numero}
                  </span>
                  <h2 className="font-display text-lg font-bold text-titulo sm:text-xl">
                    {r.titulo}
                  </h2>
                </div>
                <p className="mt-1 text-sm font-semibold text-laranja-texto">{r.chamada}</p>
                <p className="mt-2 grow text-sm leading-relaxed text-cinza">{r.descricao}</p>
                <ul className="mt-3 flex flex-wrap gap-1.5">
                  {r.marcas.map((m) => (
                    <li
                      key={m}
                      className="rounded-full bg-chip px-2.5 py-1 text-[11px] font-medium text-chip-tinta"
                    >
                      {m}
                    </li>
                  ))}
                </ul>
                <span className="pill-cta mt-4 self-start text-sm group-hover:brightness-105">
                  {r.cta}
                </span>
              </Link>
            </li>
          ))}
        </ul>

        {/*
          A seção "o que esta ferramenta não é" fica na entrada E no pé de cada
          módulo, pelo mesmo motivo do Simulador MEI: o aluno pode chegar direto
          por link e nunca ver a entrada.
        */}
        <div className="mt-8">
          <Alerta tom="atencao" titulo="O que o HUB DP não é">
            Não é sistema de folha: nada do que você digitar é gravado, e nenhum evento é
            transmitido a órgão nenhum. É uma ferramenta de estudo e de conferência — os números
            seguem as tabelas vigentes, mostradas no pé de cada módulo, e o fechamento oficial
            continua sendo o do seu sistema.
          </Alerta>
        </div>
      </div>
    </main>
  );
}

/** Um fato do hero, sobre navy. Rótulo em `navy-mute`, valor em `navy-ink`. */
function Fato({ rotulo, valor, nota }: { rotulo: string; valor: string; nota?: string }) {
  return (
    <div>
      <dt className="text-[11px] font-semibold tracking-[0.14em] text-navy-mute uppercase">
        {rotulo}
      </dt>
      <dd className="mt-0.5 font-display text-xl font-bold tabular-nums text-navy-ink">{valor}</dd>
      {nota && <dd className="text-xs text-navy-mute">{nota}</dd>}
    </div>
  );
}

/**
 * Segmento que não é módulo.
 *
 * Diz quais existem, e a lista é DERIVADA de `ROTINAS` — assim ela não pode
 * discordar do despacho. E não manda "escolha na lista ao lado": abaixo de 1024px
 * não existe lista ao lado, e texto que aponta posição mente em uma das duas
 * larguras (cicatriz medida no IR Pro).
 */
function NaoExiste({ segmento }: { segmento: string }) {
  return (
    <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
      <h1 className="font-display text-xl font-bold text-titulo">Este módulo não existe</h1>
      <p className="mt-2 text-sm leading-relaxed text-cinza">
        Não há um módulo chamado <span className="font-semibold">{segmento}</span> no HUB DP. Os que
        existem são: {ROTINAS.map((r) => r.titulo).join(", ")}.
      </p>
      <Link to="/ferramenta/hub-dp" className="pill-cta mt-5 inline-flex">
        Ver os cinco módulos
      </Link>
    </main>
  );
}
